/* eslint-disable consistent-return */
import { useEffect, useRef } from 'react'
import useDeepEqualEffect from '~/hooks/useDeepEqualEffect'

/**
 * A custom hook that executes a callback function with support for debouncing, optional initial execution,
 * leading execution, and deep dependency comparison, while observing provided dependencies and refs.
 *
 * @param callback A function to be executed after the debounce delay or immediately based on input options.
 *
 * The function receives an options object indicating the execution mode and spread refs values.
 * @param options An object for configuration.
 * @param options.delay Number of milliseconds to wait before executing the callback. Defaults to 0.
 * @param options.initial Boolean indicating whether to execute the callback on the initial mount. Defaults to false.
 * @param options.leading Boolean indicating whether to execute the callback immediately on dependency changes before delay. Defaults to false.
 * @param options.deepEquals Boolean indicating whether to use deep comparison for dependencies. Defaults to false.
 * @param deps An array of dependencies to observe for changes. Defaults to an empty array.
 * @param refs An array of refs that will be passed as arguments to the callback. Defaults to an empty array.
 *
 * @return void
 *
 * @example
 * ```tsx
 * import useDebouncedEffect from '~/hooks/useDebouncedEffect'
 * function MyComponent() {
 *   const [value, setValue] = useState('')
 *   useDebouncedEffect(
 *     (opts, someStableRefA, someStableRefB) => {
 *       // Your debounced logic here
 *       console.log('Debounced value:', value, 'Ref:', ref, 'Opts', opts.delay)
 *     },
 *     { delay: 500 },
 *     [someValueThatChanges],
 *     [someStableRefA, someStableRefB]
 *   )
 *   return (
 *     <input type="text" value={value} onChange={(e) => setValue(e.target.value)} />
 *   )
 * }
 * ```
 */
export default function useDebouncedEffect<TDeps, TRefs>(
  callback: (opts: { initial?: boolean; leading?: boolean }, ...refs: TRefs[]) => void,
  { delay = 0, initial = false, leading = false, deepEquals = false },
  deps: TDeps[] = [],
  refs: TRefs[] = []
) {
  const firstUpdate = useRef(true)
  const timeoutId = useRef<ReturnType<typeof setTimeout>>()

  const refsRef = useRef(refs)
  refsRef.current = refs

  const useEffectHook = deepEquals ? useDeepEqualEffect : useEffect

  useEffectHook(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      if (initial) callback({ initial: true, leading: true }, ...refsRef.current)
      return
    }

    if (delay > 0) {
      if (!timeoutId.current && leading) {
        callback({ leading: true }, ...refsRef.current)
      }

      timeoutId.current = setTimeout(() => {
        callback({}, ...refsRef.current)
      }, delay)

      return () => {
        if (timeoutId.current) clearTimeout(timeoutId.current)
        timeoutId.current = undefined
      }
    }

    callback({}, ...refsRef.current)
  }, deps)

  useEffect(
    () =>
      function cleanup() {
        firstUpdate.current = true
      },
    []
  )
}
